<template>
  <AdminLayout>
    <div class="notice-manage">
      <!-- 페이지 헤더 -->
      <div class="page-header">
        <div class="header-info">
          <div class="header-icon">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"/>
            </svg>
          </div>
          <div>
            <h1 class="page-title">공지사항 관리</h1>
            <p class="page-subtitle">사용자에게 공지할 내용을 관리합니다</p>
          </div>
        </div>
        <button class="add-btn" @click="openCreateModal">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          새 공지
        </button>
      </div>

      <!-- 통계 요약 -->
      <section class="stats-section">
        <div class="stat-card">
          <span class="stat-value">{{ notices.length }}</span>
          <span class="stat-label">전체</span>
        </div>
        <div class="stat-card">
          <span class="stat-value">{{ activeCount }}</span>
          <span class="stat-label">활성</span>
        </div>
        <div class="stat-card">
          <span class="stat-value">{{ importantCount }}</span>
          <span class="stat-label">중요</span>
        </div>
      </section>

      <!-- 공지사항 목록 -->
      <section class="notice-list">
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>로딩 중...</p>
        </div>

        <div v-else-if="notices.length === 0" class="empty-state">
          <span class="empty-icon">📢</span>
          <p>등록된 공지사항이 없습니다</p>
          <button class="create-first-btn" @click="openCreateModal">
            첫 번째 공지사항 등록하기
          </button>
        </div>

        <div v-else class="notice-items">
          <div
            v-for="notice in notices"
            :key="notice.id"
            class="notice-item"
            :class="{ inactive: !notice.isActive }"
          >
            <div class="notice-header">
              <div class="notice-badges">
                <span v-if="notice.isImportant" class="badge important">중요</span>
                <span v-if="!notice.isActive" class="badge inactive">비활성</span>
              </div>
              <div class="notice-actions">
                <button
                  class="action-btn"
                  :class="notice.isActive ? 'deactivate' : 'activate'"
                  @click="handleToggleActive(notice)"
                >
                  {{ notice.isActive ? '비활성화' : '활성화' }}
                </button>
                <button class="action-btn edit" @click="openEditModal(notice)">수정</button>
                <button class="action-btn delete" @click="handleDelete(notice)">삭제</button>
              </div>
            </div>

            <h3 class="notice-title">{{ notice.title }}</h3>

            <div class="notice-meta">
              <span class="meta-item">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
                {{ notice.viewCount?.toLocaleString() || 0 }}
              </span>
              <span class="meta-item">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
                {{ formatDate(notice.createdAt) }}
              </span>
            </div>

            <div class="notice-preview" v-html="getPreviewContent(notice.content)"></div>
          </div>
        </div>
      </section>

      <!-- 공지사항 생성/수정 모달 -->
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal-container">
          <div class="modal-header">
            <h2 class="modal-title">{{ isEditing ? '공지사항 수정' : '새 공지사항' }}</h2>
            <button class="close-btn" @click="closeModal">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <div class="form-group">
              <label class="form-label">제목 <span class="required">*</span></label>
              <input
                v-model="form.title"
                type="text"
                class="form-input"
                placeholder="공지사항 제목을 입력하세요"
              />
            </div>

            <div class="form-group">
              <label class="form-label">내용 <span class="required">*</span></label>

              <!-- WYSIWYG 에디터 -->
              <div class="wysiwyg-editor">
                <!-- 에디터 툴바 -->
                <div class="editor-toolbar">
                  <!-- 텍스트 스타일 -->
                  <div class="toolbar-group">
                    <button
                      type="button"
                      class="toolbar-btn"
                      :class="{ active: isFormatActive('bold') }"
                      @click="execCommand('bold')"
                      title="굵게 (Ctrl+B)"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      :class="{ active: isFormatActive('italic') }"
                      @click="execCommand('italic')"
                      title="기울임 (Ctrl+I)"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      :class="{ active: isFormatActive('underline') }"
                      @click="execCommand('underline')"
                      title="밑줄 (Ctrl+U)"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      :class="{ active: isFormatActive('strikeThrough') }"
                      @click="execCommand('strikeThrough')"
                      title="취소선"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M10 19h4v-3h-4v3zM5 4v3h5v3h4V7h5V4H5zM3 14h18v-2H3v2z"/>
                      </svg>
                    </button>
                  </div>

                  <span class="toolbar-divider"></span>

                  <!-- 글자 크기 -->
                  <div class="toolbar-group">
                    <select class="toolbar-select" @change="changeFontSize($event)" title="글자 크기">
                      <option value="">크기</option>
                      <option value="1">아주 작게</option>
                      <option value="2">작게</option>
                      <option value="3">보통</option>
                      <option value="4">크게</option>
                      <option value="5">아주 크게</option>
                      <option value="6">매우 크게</option>
                      <option value="7">최대</option>
                    </select>
                  </div>

                  <span class="toolbar-divider"></span>

                  <!-- 글자 색상 -->
                  <div class="toolbar-group">
                    <div class="color-picker-wrapper">
                      <button type="button" class="toolbar-btn color-btn" title="글자 색상">
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M11 2L5.5 16h2.25l1.12-3h6.25l1.12 3h2.25L13 2h-2zm-1.38 9L12 4.67 14.38 11H9.62z"/>
                        </svg>
                        <span class="color-indicator" :style="{ backgroundColor: currentTextColor }"></span>
                      </button>
                      <input type="color" class="color-input" v-model="currentTextColor" @input="changeTextColor" title="글자 색상 선택">
                    </div>
                    <div class="color-picker-wrapper">
                      <button type="button" class="toolbar-btn color-btn" title="배경 색상">
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M16.56 8.94L7.62 0 6.21 1.41l2.38 2.38-5.15 5.15c-.59.59-.59 1.54 0 2.12l5.5 5.5c.29.29.68.44 1.06.44s.77-.15 1.06-.44l5.5-5.5c.59-.58.59-1.53 0-2.12zM5.21 10L10 5.21 14.79 10H5.21zM19 11.5s-2 2.17-2 3.5c0 1.1.9 2 2 2s2-.9 2-2c0-1.33-2-3.5-2-3.5z"/>
                          <path d="M0 20h24v4H0z" :fill="currentBgColor"/>
                        </svg>
                      </button>
                      <input type="color" class="color-input" v-model="currentBgColor" @input="changeBgColor" title="배경 색상 선택">
                    </div>
                  </div>

                  <span class="toolbar-divider"></span>

                  <!-- 정렬 -->
                  <div class="toolbar-group">
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="execCommand('justifyLeft')"
                      title="왼쪽 정렬"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="execCommand('justifyCenter')"
                      title="가운데 정렬"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M7 15v2h10v-2H7zm-4 6h18v-2H3v2zm0-8h18v-2H3v2zm4-6v2h10V7H7zM3 3v2h18V3H3z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="execCommand('justifyRight')"
                      title="오른쪽 정렬"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 21h18v-2H3v2zm6-4h12v-2H9v2zm-6-4h18v-2H3v2zm6-4h12V7H9v2zM3 3v2h18V3H3z"/>
                      </svg>
                    </button>
                  </div>

                  <span class="toolbar-divider"></span>

                  <!-- 목록 -->
                  <div class="toolbar-group">
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="execCommand('insertUnorderedList')"
                      title="글머리 기호"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="execCommand('insertOrderedList')"
                      title="번호 매기기"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z"/>
                      </svg>
                    </button>
                  </div>

                  <span class="toolbar-divider"></span>

                  <!-- 들여쓰기 -->
                  <div class="toolbar-group">
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="execCommand('outdent')"
                      title="내어쓰기"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M11 17h10v-2H11v2zm-8-5l4 4V8l-4 4zm0 9h18v-2H3v2zM3 3v2h18V3H3zm8 6h10V7H11v2zm0 4h10v-2H11v2z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="execCommand('indent')"
                      title="들여쓰기"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 21h18v-2H3v2zM3 8v8l4-4-4-4zm8 9h10v-2H11v2zM3 3v2h18V3H3zm8 6h10V7H11v2zm0 4h10v-2H11v2z"/>
                      </svg>
                    </button>
                  </div>

                  <span class="toolbar-divider"></span>

                  <!-- 링크, 구분선 -->
                  <div class="toolbar-group">
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="insertLink"
                      title="링크 삽입"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="insertHorizontalRule"
                      title="구분선 삽입"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M4 11h16v2H4z"/>
                      </svg>
                    </button>
                  </div>

                  <span class="toolbar-divider"></span>

                  <!-- 실행 취소/다시 실행 -->
                  <div class="toolbar-group">
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="execCommand('undo')"
                      title="실행 취소 (Ctrl+Z)"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="execCommand('redo')"
                      title="다시 실행 (Ctrl+Y)"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z"/>
                      </svg>
                    </button>
                  </div>

                  <span class="toolbar-divider"></span>

                  <!-- HTML 소스 보기 토글 -->
                  <div class="toolbar-group">
                    <button
                      type="button"
                      class="toolbar-btn"
                      :class="{ active: showHtmlSource }"
                      @click="toggleHtmlSource"
                      title="HTML 소스 보기"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- 에디터 영역 -->
                <div
                  v-show="!showHtmlSource"
                  ref="editorRef"
                  class="editor-content"
                  contenteditable="true"
                  @input="onEditorInput"
                  @paste="onPaste"
                  @keydown="onKeydown"
                  @mouseup="updateToolbarState"
                  @keyup="updateToolbarState"
                ></div>

                <!-- HTML 소스 편집 -->
                <textarea
                  v-show="showHtmlSource"
                  ref="htmlSourceRef"
                  v-model="htmlSource"
                  class="html-source"
                  @input="onHtmlSourceInput"
                  placeholder="HTML 소스를 직접 편집할 수 있습니다"
                ></textarea>
              </div>
            </div>

            <div class="form-group checkbox-group">
              <label class="checkbox-label">
                <input v-model="form.isImportant" type="checkbox" />
                <span class="checkmark"></span>
                <span class="label-text">중요 공지로 설정 (목록 상단에 고정)</span>
              </label>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn-cancel" @click="closeModal">취소</button>
            <button
              class="btn-submit"
              :disabled="!isFormValid || submitting"
              @click="handleSubmit"
            >
              <span v-if="submitting">저장 중...</span>
              <span v-else>{{ isEditing ? '수정하기' : '등록하기' }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- 삭제 확인 모달 -->
      <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="showDeleteConfirm = false">
        <div class="confirm-modal">
          <div class="confirm-icon">
            <svg class="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
          </div>
          <h3 class="confirm-title">공지사항 삭제</h3>
          <p class="confirm-message">
            "{{ deleteTarget?.title }}"<br/>
            정말 삭제하시겠습니까?
          </p>
          <div class="confirm-actions">
            <button class="btn-cancel" @click="showDeleteConfirm = false">취소</button>
            <button class="btn-delete" @click="confirmDelete">삭제</button>
          </div>
        </div>
      </div>

      <!-- 토스트 메시지 -->
      <transition name="toast">
        <div v-if="showToast" class="toast" :class="toastType">
          {{ toastMessage }}
        </div>
      </transition>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useNotice } from '@/composables/common/useNotice'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'

export default {
  name: 'NoticeManageView',
  components: {
    AdminLayout
  },
  setup() {
    const { loading, getAllNotices, createNotice, updateNotice, toggleNoticeActive, deleteNotice } = useNotice()

    const notices = ref([])
    const showModal = ref(false)
    const isEditing = ref(false)
    const editingId = ref(null)
    const submitting = ref(false)

    // WYSIWYG 에디터 관련
    const editorRef = ref(null)
    const htmlSourceRef = ref(null)
    const showHtmlSource = ref(false)
    const htmlSource = ref('')
    const currentTextColor = ref('#000000')
    const currentBgColor = ref('#ffff00')
    const activeFormats = ref({
      bold: false,
      italic: false,
      underline: false,
      strikeThrough: false
    })

    const form = ref({
      title: '',
      content: '',
      isImportant: false
    })

    // 삭제 확인
    const showDeleteConfirm = ref(false)
    const deleteTarget = ref(null)

    // 토스트
    const showToast = ref(false)
    const toastMessage = ref('')
    const toastType = ref('success')

    // 통계
    const activeCount = computed(() => notices.value.filter(n => n.isActive).length)
    const importantCount = computed(() => notices.value.filter(n => n.isImportant).length)

    // 폼 유효성
    const isFormValid = computed(() => {
      return form.value.title.trim() !== '' && form.value.content.trim() !== ''
    })

    // 공지사항 로드
    const loadNotices = async () => {
      notices.value = await getAllNotices()
    }

    // 날짜 포맷
    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    // 미리보기 콘텐츠 (100자 제한)
    const getPreviewContent = (content) => {
      if (!content) return ''
      const text = content.replace(/<[^>]*>/g, '') // HTML 태그 제거
      return text.length > 100 ? text.substring(0, 100) + '...' : text
    }

    // WYSIWYG 에디터: execCommand 실행
    const execCommand = (command, value = null) => {
      editorRef.value?.focus()
      document.execCommand(command, false, value)
      updateToolbarState()
      syncEditorContent()
    }

    // 현재 포맷 상태 확인
    const isFormatActive = (format) => {
      return activeFormats.value[format]
    }

    // 툴바 상태 업데이트
    const updateToolbarState = () => {
      activeFormats.value.bold = document.queryCommandState('bold')
      activeFormats.value.italic = document.queryCommandState('italic')
      activeFormats.value.underline = document.queryCommandState('underline')
      activeFormats.value.strikeThrough = document.queryCommandState('strikeThrough')
    }

    // 에디터 내용을 form.content에 동기화
    const syncEditorContent = () => {
      if (editorRef.value) {
        form.value.content = editorRef.value.innerHTML
      }
    }

    // 에디터 입력 이벤트
    const onEditorInput = () => {
      syncEditorContent()
    }

    // 붙여넣기 이벤트 (순수 텍스트만 붙여넣기 옵션)
    const onPaste = () => {
      // 기본 붙여넣기 허용 (HTML 유지)
      // 필요시 아래 코드로 텍스트만 붙여넣기 가능
      // e.preventDefault()
      // const text = e.clipboardData.getData('text/plain')
      // document.execCommand('insertText', false, text)
    }

    // 키보드 단축키
    const onKeydown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'b':
            e.preventDefault()
            execCommand('bold')
            break
          case 'i':
            e.preventDefault()
            execCommand('italic')
            break
          case 'u':
            e.preventDefault()
            execCommand('underline')
            break
        }
      }
    }

    // 글자 크기 변경
    const changeFontSize = (e) => {
      const size = e.target.value
      if (size) {
        execCommand('fontSize', size)
        e.target.value = ''
      }
    }

    // 글자 색상 변경
    const changeTextColor = () => {
      execCommand('foreColor', currentTextColor.value)
    }

    // 배경 색상 변경
    const changeBgColor = () => {
      execCommand('hiliteColor', currentBgColor.value)
    }

    // 링크 삽입
    const insertLink = () => {
      const selection = window.getSelection()
      const selectedText = selection.toString() || '링크'

      const url = prompt('링크 URL을 입력하세요:', 'https://')
      if (!url) return

      if (selection.toString()) {
        execCommand('createLink', url)
      } else {
        const linkHtml = `<a href="${url}" target="_blank">${selectedText}</a>`
        execCommand('insertHTML', linkHtml)
      }
    }

    // 구분선 삽입
    const insertHorizontalRule = () => {
      execCommand('insertHorizontalRule')
    }

    // HTML 소스 보기 토글
    const toggleHtmlSource = () => {
      if (showHtmlSource.value) {
        // 소스 → 에디터
        if (editorRef.value) {
          editorRef.value.innerHTML = htmlSource.value
          syncEditorContent()
        }
      } else {
        // 에디터 → 소스
        htmlSource.value = form.value.content
      }
      showHtmlSource.value = !showHtmlSource.value
    }

    // HTML 소스 입력
    const onHtmlSourceInput = () => {
      form.value.content = htmlSource.value
    }

    // 에디터에 내용 설정
    const setEditorContent = (content) => {
      nextTick(() => {
        if (editorRef.value) {
          editorRef.value.innerHTML = content || ''
        }
      })
    }

    // 모달 열기 (생성)
    const openCreateModal = () => {
      isEditing.value = false
      editingId.value = null
      showHtmlSource.value = false
      htmlSource.value = ''
      form.value = {
        title: '',
        content: '',
        isImportant: false
      }
      showModal.value = true
      setEditorContent('')
    }

    // 모달 열기 (수정)
    const openEditModal = (notice) => {
      isEditing.value = true
      editingId.value = notice.id
      showHtmlSource.value = false
      htmlSource.value = notice.content || ''
      form.value = {
        title: notice.title,
        content: notice.content,
        isImportant: notice.isImportant
      }
      showModal.value = true
      setEditorContent(notice.content)
    }

    // 모달 닫기
    const closeModal = () => {
      showModal.value = false
      showHtmlSource.value = false
      htmlSource.value = ''
      form.value = {
        title: '',
        content: '',
        isImportant: false
      }
    }

    // 제출 처리
    const handleSubmit = async () => {
      if (!isFormValid.value || submitting.value) return

      submitting.value = true

      try {
        if (isEditing.value) {
          await updateNotice(editingId.value, form.value)
          showToastMessage('공지사항이 수정되었습니다.', 'success')
        } else {
          await createNotice(form.value)
          showToastMessage('공지사항이 등록되었습니다.', 'success')
        }

        closeModal()
        await loadNotices()
      } catch (err) {
        showToastMessage('처리 중 오류가 발생했습니다.', 'error')
      } finally {
        submitting.value = false
      }
    }

    // 활성화 토글
    const handleToggleActive = async (notice) => {
      try {
        await toggleNoticeActive(notice.id, !notice.isActive)
        notice.isActive = !notice.isActive
        showToastMessage(
          notice.isActive ? '공지사항이 활성화되었습니다.' : '공지사항이 비활성화되었습니다.',
          'success'
        )
      } catch (err) {
        showToastMessage('처리 중 오류가 발생했습니다.', 'error')
      }
    }

    // 삭제 요청
    const handleDelete = (notice) => {
      deleteTarget.value = notice
      showDeleteConfirm.value = true
    }

    // 삭제 확인
    const confirmDelete = async () => {
      if (!deleteTarget.value) return

      try {
        await deleteNotice(deleteTarget.value.id)
        showToastMessage('공지사항이 삭제되었습니다.', 'success')
        showDeleteConfirm.value = false
        deleteTarget.value = null
        await loadNotices()
      } catch (err) {
        showToastMessage('삭제 중 오류가 발생했습니다.', 'error')
      }
    }

    // 토스트 메시지
    const showToastMessage = (message, type = 'success') => {
      toastMessage.value = message
      toastType.value = type
      showToast.value = true
      setTimeout(() => {
        showToast.value = false
      }, 3000)
    }

    onMounted(() => {
      loadNotices()
    })

    return {
      loading,
      notices,
      showModal,
      isEditing,
      form,
      submitting,
      isFormValid,
      activeCount,
      importantCount,
      showDeleteConfirm,
      deleteTarget,
      showToast,
      toastMessage,
      toastType,
      formatDate,
      getPreviewContent,
      openCreateModal,
      openEditModal,
      closeModal,
      handleSubmit,
      handleToggleActive,
      handleDelete,
      confirmDelete,
      // WYSIWYG 에디터
      editorRef,
      htmlSourceRef,
      showHtmlSource,
      htmlSource,
      currentTextColor,
      currentBgColor,
      execCommand,
      isFormatActive,
      updateToolbarState,
      onEditorInput,
      onPaste,
      onKeydown,
      changeFontSize,
      changeTextColor,
      changeBgColor,
      insertLink,
      insertHorizontalRule,
      toggleHtmlSource,
      onHtmlSourceInput
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.notice-manage {
  @apply p-6 lg:p-8;
}

/* 페이지 헤더 */
.page-header {
  @apply flex items-center justify-between mb-8;
}

.header-info {
  @apply flex items-center gap-3;
}

.header-icon {
  @apply w-12 h-12 bg-blue-600/10 rounded-xl flex items-center justify-center text-blue-600;
}

.page-title {
  @apply text-2xl font-bold text-slate-800;
}

.page-subtitle {
  @apply text-sm text-slate-500;
}

.add-btn {
  @apply flex items-center gap-1.5 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition shadow-lg shadow-blue-600/30;
}

/* 통계 */
.stats-section {
  @apply flex gap-4 mb-6;
}

.stat-card {
  @apply flex-1 flex flex-col items-center justify-center py-5 bg-white rounded-xl border border-slate-200 shadow-sm;
}

.stat-value {
  @apply text-3xl font-bold text-slate-800;
}

.stat-label {
  @apply text-sm text-slate-500 mt-1;
}

/* 목록 */
.notice-list {
  @apply bg-white rounded-2xl border border-slate-200 shadow-sm p-6;
}

.loading-state,
.empty-state {
  @apply flex flex-col items-center justify-center py-16 text-slate-500;
}

.spinner {
  @apply w-8 h-8 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin mb-4;
}

.empty-icon {
  @apply text-5xl mb-4;
}

.create-first-btn {
  @apply mt-4 px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition;
}

.notice-items {
  @apply space-y-4;
}

.notice-item {
  @apply bg-slate-50 rounded-xl p-5 border border-slate-200 transition hover:border-blue-300;
}

.notice-item.inactive {
  @apply opacity-60;
}

.notice-header {
  @apply flex items-center justify-between mb-3;
}

.notice-badges {
  @apply flex gap-2;
}

.badge {
  @apply px-2.5 py-1 text-xs font-semibold rounded-lg;
}

.badge.important {
  @apply bg-red-100 text-red-600;
}

.badge.inactive {
  @apply bg-slate-200 text-slate-500;
}

.notice-actions {
  @apply flex gap-2;
}

.action-btn {
  @apply px-3 py-1.5 text-xs font-medium rounded-lg transition;
}

.action-btn.edit {
  @apply bg-slate-200 text-slate-600 hover:bg-slate-300;
}

.action-btn.delete {
  @apply bg-red-100 text-red-600 hover:bg-red-200;
}

.action-btn.activate {
  @apply bg-green-100 text-green-600 hover:bg-green-200;
}

.action-btn.deactivate {
  @apply bg-amber-100 text-amber-600 hover:bg-amber-200;
}

.notice-title {
  @apply text-lg font-semibold text-slate-800 mb-2;
}

.notice-meta {
  @apply flex items-center gap-4 text-sm text-slate-500 mb-3;
}

.meta-item {
  @apply flex items-center gap-1.5;
}

.notice-preview {
  @apply text-sm text-slate-600 line-clamp-2;
}

/* 모달 */
.modal-overlay {
  @apply fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4;
}

.modal-container {
  @apply w-full max-w-2xl bg-white rounded-2xl overflow-hidden max-h-[90vh] flex flex-col;
}

.modal-header {
  @apply flex items-center justify-between px-6 py-4 border-b border-slate-100;
}

.modal-title {
  @apply text-xl font-bold text-slate-800;
}

.close-btn {
  @apply p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition;
}

.modal-body {
  @apply flex-1 overflow-y-auto p-6 space-y-5;
}

.form-group {
  @apply space-y-2;
}

.form-label {
  @apply block text-sm font-semibold text-slate-700;
}

.required {
  @apply text-red-500;
}

.form-input {
  @apply w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none transition focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20;
}

/* WYSIWYG 에디터 */
.wysiwyg-editor {
  @apply border border-slate-200 rounded-xl overflow-hidden bg-white;
}

.editor-toolbar {
  @apply flex flex-wrap items-center gap-1 p-2 bg-slate-50 border-b border-slate-200;
}

.toolbar-group {
  @apply flex items-center gap-0.5;
}

.toolbar-btn {
  @apply w-8 h-8 flex items-center justify-center text-slate-600 hover:bg-white hover:text-blue-600 rounded-lg transition;
}

.toolbar-btn.active {
  @apply bg-blue-100 text-blue-600;
}

.toolbar-btn.color-btn {
  @apply relative;
}

.color-indicator {
  @apply absolute bottom-1 left-1/2 -translate-x-1/2 w-4 h-1 rounded-full;
}

.color-picker-wrapper {
  @apply relative;
}

.color-input {
  @apply absolute inset-0 w-full h-full opacity-0 cursor-pointer;
}

.toolbar-select {
  @apply h-8 px-2 text-xs bg-white border border-slate-200 rounded-lg text-slate-600 outline-none cursor-pointer hover:border-blue-300 focus:border-blue-600;
}

.toolbar-divider {
  @apply w-px h-6 bg-slate-200 mx-1;
}

.editor-content {
  @apply min-h-[300px] max-h-[400px] overflow-y-auto p-4 text-sm text-slate-700 leading-relaxed outline-none;
}

.editor-content:empty::before {
  content: '공지사항 내용을 입력하세요...';
  @apply text-slate-400;
}

.editor-content:focus {
  @apply bg-blue-50/30;
}

/* 에디터 내부 스타일 */
.editor-content :deep(h1),
.editor-content :deep(h2),
.editor-content :deep(h3) {
  @apply font-bold text-slate-800 mb-2;
}

.editor-content :deep(h1) {
  @apply text-2xl;
}

.editor-content :deep(h2) {
  @apply text-xl;
}

.editor-content :deep(h3) {
  @apply text-lg;
}

.editor-content :deep(p) {
  @apply mb-3;
}

.editor-content :deep(ul),
.editor-content :deep(ol) {
  @apply pl-6 mb-3;
}

.editor-content :deep(ul) {
  @apply list-disc;
}

.editor-content :deep(ol) {
  @apply list-decimal;
}

.editor-content :deep(li) {
  @apply mb-1;
}

.editor-content :deep(hr) {
  @apply my-4 border-slate-200;
}

.editor-content :deep(a) {
  @apply text-blue-600 underline hover:text-blue-700;
}

.editor-content :deep(blockquote) {
  @apply pl-4 border-l-4 border-slate-200 italic text-slate-600 my-3;
}

/* HTML 소스 편집 */
.html-source {
  @apply w-full min-h-[300px] max-h-[400px] p-4 text-sm font-mono text-slate-700 bg-slate-900 text-green-400 outline-none resize-none;
}

.checkbox-group {
  @apply pt-2;
}

.checkbox-label {
  @apply flex items-center gap-3 cursor-pointer;
}

.checkbox-label input {
  @apply w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-600;
}

.label-text {
  @apply text-sm text-slate-700;
}

.modal-footer {
  @apply flex gap-3 px-6 py-4 border-t border-slate-100;
}

.btn-cancel {
  @apply flex-1 py-3 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 transition;
}

.btn-submit {
  @apply flex-1 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition disabled:bg-slate-300 disabled:cursor-not-allowed;
}

/* 삭제 확인 모달 */
.confirm-modal {
  @apply w-full max-w-sm bg-white rounded-2xl p-6 text-center;
}

.confirm-icon {
  @apply flex justify-center mb-4;
}

.confirm-title {
  @apply text-lg font-bold text-slate-800 mb-2;
}

.confirm-message {
  @apply text-sm text-slate-600 mb-6;
}

.confirm-actions {
  @apply flex gap-3;
}

.btn-delete {
  @apply flex-1 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition;
}

/* 토스트 */
.toast {
  @apply fixed bottom-8 left-1/2 -translate-x-1/2 px-6 py-3 rounded-full text-sm font-medium shadow-lg z-50;
}

.toast.success {
  @apply bg-green-600 text-white;
}

.toast.error {
  @apply bg-red-600 text-white;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}
</style>
